// src/services/GoogleCalendarAPIService.js - SECURE VERSION for Python Backend
// Updated to call your existing Python Cloud Function with multiple operations

export class GoogleCalendarAPIService {
  
  // ==========================================
  // SECURE BACKEND INTEGRATION
  // ==========================================
  
  static getBackendURL() {
    // Your existing Cloud Function endpoint
    return 'https://create-business-calendar-652532757120.us-central1.run.app';
  }

  static getAvailabilityURL() {
    return 'https://lisa-agent-652532757120.us-central1.run.app';
  }

  /**
   * Make secure API call to Python backend
   */
  static async callBackendAPI(operation, data = {}) {
    try {
      const response = await fetch(this.getBackendURL(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: operation,
          ...data
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API call failed: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error(`Backend API call failed for operation ${operation}:`, error);
      throw error;
    }
  }

  // ==========================================
  // AUTHENTICATION (Now Backend-Based)
  // ==========================================

  /**
   * Check if calendar is authenticated (via backend)
   */
  static async isAuthenticated() {
    try {
      // Authentication is now handled by backend service account
      // Just verify backend is accessible
      const result = await this.callBackendAPI('get-events', { 
        businessId: 'test' 
      });
      
      return result.success !== false; // Backend handles auth automatically
    } catch (error) {
      console.warn('Calendar authentication check failed:', error);
      return false;
    }
  }

  /**
   * Initialize calendar integration (calls Python backend)
   */
  static async initializeCalendarIntegration(businessId, businessData) {
    try {
      console.log('🔧 Initializing calendar integration via Python backend...');
      
      const result = await this.callBackendAPI('create-calendar', {
        businessId: businessId,
        businessName: businessData.business_name || businessData.businessName,
        ownerEmail: businessData.owner_email || businessData.ownerEmail,
        timezone: businessData.timezone || 'America/Toronto'
      });

      if (result.success) {
        console.log('✅ Calendar integration initialized:', result.calendarId);
        return {
          success: true,
          calendarId: result.calendarId,
          publicUrl: result.calendarUrl,
          icsUrl: result.icsUrl,
          message: result.message
        };
      } else {
        throw new Error(result.error || 'Calendar setup failed');
      }
    } catch (error) {
      console.error('❌ Calendar integration failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // EVENT MANAGEMENT (Secure Backend Calls)
  // ==========================================

  /**
   * Create calendar event via secure Python backend
   */
  static async createCalendarEvent(calendarId, bookingData) {
    try {
      console.log('📅 Creating calendar event via Python backend for booking:', bookingData.id);
      
      const result = await this.callBackendAPI('sync-booking', {
        businessId: bookingData.business_id,
        booking: bookingData
      });

      if (result.success) {
        console.log('✅ Calendar event created:', result.eventId);
        return {
          success: true,
          eventId: result.eventId,
          htmlLink: result.htmlLink,
          event: {
            id: result.eventId,
            htmlLink: result.htmlLink
          }
        };
      } else {
        throw new Error(result.error || 'Event creation failed');
      }
    } catch (error) {
      console.error('❌ Failed to create calendar event:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update calendar event via secure Python backend
   */
  static async updateCalendarEvent(calendarId, eventId, bookingData) {
    try {
      console.log('🔄 Updating calendar event via Python backend:', eventId);
      
      const result = await this.callBackendAPI('update-event', {
        businessId: bookingData.business_id,
        booking: bookingData,
        eventId: eventId
      });

      if (result.success) {
        console.log('✅ Calendar event updated:', eventId);
        return {
          success: true,
          eventId: result.eventId,
          event: { id: result.eventId }
        };
      } else {
        throw new Error(result.error || 'Event update failed');
      }
    } catch (error) {
      console.error('❌ Failed to update calendar event:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete calendar event via secure Python backend
   */
  static async deleteCalendarEvent(calendarId, eventId, bookingId, businessId) {
    try {
      console.log('🗑️ Deleting calendar event via Python backend:', eventId);
      
      const result = await this.callBackendAPI('delete-event', {
        businessId: businessId,
        bookingId: bookingId,
        eventId: eventId
      });

      if (result.success) {
        console.log('✅ Calendar event deleted:', eventId);
        return {
          success: true,
          message: result.message
        };
      } else {
        throw new Error(result.error || 'Event deletion failed');
      }
    } catch (error) {
      console.error('❌ Failed to delete calendar event:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get calendar events via secure Python backend
   */
  static async getCalendarEvents(businessId, startDate, endDate) {
    try {
      console.log('📋 Getting calendar events via Python backend...');
      
      // Build query parameters for GET request
      const params = new URLSearchParams({
        operation: 'get-events',
        businessId: businessId
      });
      
      if (startDate) {
        params.append('startDate', startDate);
      }
      
      if (endDate) {
        params.append('endDate', endDate);
      }

      // Use GET request for fetching events
      const response = await fetch(`${this.getBackendURL()}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get calendar events: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('✅ Retrieved calendar events:', result.events.length);
        return {
          success: true,
          events: result.events
        };
      } else {
        return {
          success: false,
          events: [],
          error: result.error
        };
      }
      
    } catch (error) {
      console.error('❌ Failed to get calendar events:', error);
      return {
        success: false,
        events: [],
        error: error.message
      };
    }
  }

  /**
   * Sync all bookings to calendar via secure Python backend
   */
  static async syncAllBookingsToCalendar(businessId) {
    try {
      console.log('🔄 Syncing all bookings via Python backend...');
      
      const result = await this.callBackendAPI('sync-all-bookings', {
        businessId: businessId
      });

      if (result.success) {
        console.log('✅ All bookings synced:', result.results);
        return {
          success: true,
          results: result.results,
          message: result.message
        };
      } else {
        throw new Error(result.error || 'Bulk sync failed');
      }
    } catch (error) {
      console.error('❌ Failed to sync all bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // HELPER METHODS (Preserved from Original)
  // ==========================================

  /**
   * Build event description - preserved from original logic
   */
  static buildEventDescription(bookingData) {
    const lines = [
      `📋 Service: ${bookingData.service_name}`,
      `👤 Customer: ${bookingData.customer_name}`,
      `📧 Email: ${bookingData.customer_email || 'Not provided'}`,
      `📞 Phone: ${bookingData.customer_phone || 'Not provided'}`,
      `⏰ Duration: ${bookingData.duration_minutes || 60} minutes`,
      `💰 Price: $${bookingData.price || 0}`
    ];

    if (bookingData.notes) {
      lines.push(`📝 Notes: ${bookingData.notes}`);
    }

    lines.push('', '🔗 Managed by Booker.Chat');
    return lines.join('\n');
  }

  /**
   * Generate calendar subscription URLs - preserved logic
   */
  static generateSubscriptionURLs(calendarId, businessName) {
    const encodedName = encodeURIComponent(`${businessName} - Appointments`);
    
    return {
      googleCalendar: `https://calendar.google.com/calendar/embed?src=${calendarId}`,
      iCalURL: `https://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
      outlookSubscription: `https://outlook.live.com/calendar/addcalendar?name=${encodedName}&url=https://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
      appleCalendarURL: `webcal://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
      
      instructions: {
        apple: 'Open Calendar app → File → New Calendar Subscription → Paste iCal URL',
        outlook: 'Open Outlook → Calendar → Add Calendar → Subscribe from web → Paste iCal URL',
        google: 'Already integrated - view in your Google Calendar',
        android: 'Open Google Calendar app - calendar will sync automatically',
        other: 'Use iCal URL with any calendar app that supports subscriptions'
      }
    };
  }

  // ==========================================
  // LEGACY METHOD COMPATIBILITY
  // ==========================================

  /**
   * For backwards compatibility with existing code
   */
  static async useExistingCalendar(calendarId) {
    console.log('📋 Using existing calendar (Python backend managed):', calendarId);
    return {
      success: true,
      calendarId: calendarId,
      message: 'Calendar integration active'
    };
  }

  // REMOVED: All client-side Google API methods are now handled by Python backend
  // - No more loadGoogleAPI()
  // - No more initializeWithServiceAccount() 
  // - No more getServiceAccountAccessToken()
  // - No more createServiceAccountJWT()
}


// ==========================================
// UPDATED FirebaseService.js Methods (Add These)
// ==========================================

/*
Add these methods to your existing FirebaseService.js:

import { doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';

// Add to FirebaseService class:

static async storeCalendarEventMapping(businessId, bookingId, mappingData) {
  try {
    const mappingRef = doc(db, 'calendar_events', `${businessId}_${bookingId}`);
    await setDoc(mappingRef, mappingData);
    console.log('✅ Calendar event mapping stored');
  } catch (error) {
    console.error('❌ Failed to store calendar event mapping:', error);
    throw error;
  }
}

static async removeCalendarEventMapping(businessId, bookingId) {
  try {
    const mappingRef = doc(db, 'calendar_events', `${businessId}_${bookingId}`);
    await deleteDoc(mappingRef);
    console.log('✅ Calendar event mapping removed');
  } catch (error) {
    console.error('❌ Failed to remove calendar event mapping:', error);
  }
}

static async getCalendarEventMapping(businessId, bookingId) {
  try {
    const mappingRef = doc(db, 'calendar_events', `${businessId}_${bookingId}`);
    const mappingDoc = await getDoc(mappingRef);
    
    if (mappingDoc.exists()) {
      return { exists: true, data: mappingDoc.data() };
    } else {
      return { exists: false, data: null };
    }
  } catch (error) {
    console.error('❌ Failed to get calendar event mapping:', error);
    return { exists: false, data: null };
  }
}
*/
